import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import {
  actorSchema,
  type TravelTrip,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { and, eq, isNull } from "@-/com.npmjs.drizzle-orm";
import { DRAGON_TRAVEL_MODE, readHanWallet } from "@-/dev.improve.haniverse.lib.han-currency";
import { uuidv4 } from "@-/com.npmjs.uuid";
import type { ChinaTransport } from "@-/dev.improve.haniverse.data.china-transport";
import {
  canTravelTo,
  deriveTravelState,
  START_TRAVEL_MODE,
  TELEPORT_TRAVEL_MODE,
  type TravelState,
} from "#derive-travel-state";
import { resolveTravelOptions } from "#resolve-travel-options";
import { TravelError } from "#travel-error";
import { travelOptionHanMilesRedemption } from "#travel-han-miles";

async function readTrips(tx: ActorTransaction): Promise<TravelTrip[]> {
  return tx.query.travelTrip.findMany({});
}

async function readState(tx: ActorTransaction, now: Date): Promise<TravelState> {
  return deriveTravelState(await readTrips(tx), now);
}

function requireAt(state: TravelState): number {
  if (state.kind === "in-transit") {
    throw new TravelError("in-transit");
  }
  if (state.kind === "unplaced") {
    throw new TravelError("unplaced");
  }
  return state.adcode;
}

async function insertTrip(
  tx: ActorTransaction,
  now: Date,
  trip: Omit<
    typeof actorSchema.travelTrip.$inferInsert,
    "cancelledAt" | "createdAt" | "departedAt" | "id"
  >,
): Promise<string> {
  const id = uuidv4();
  const nowIso = now.toISOString();
  await tx
    .insert(actorSchema.travelTrip)
    .values({ ...trip, cancelledAt: null, createdAt: nowIso, departedAt: nowIso, id })
    .run();
  return id;
}

/** Places the learner for the first time, at a place whose name they have learned. */
export async function chooseStartingPlace({
  actorDb,
  adcode,
  now,
  unlocked,
}: {
  actorDb: ActorDatabaseHandle;
  adcode: number;
  now: Date;
  unlocked: boolean;
}): Promise<string> {
  return actorDb.transaction(async (tx) => {
    const state = await readState(tx, now);
    if (state.kind !== "unplaced") {
      throw new TravelError("already-placed");
    }
    if (!unlocked) {
      throw new TravelError("locked");
    }
    return insertTrip(tx, now, {
      arrivesAt: now.toISOString(),
      costHan: 0,
      destinationAdcode: adcode,
      distanceKm: 0,
      fareFen: 0,
      mode: START_TRAVEL_MODE,
      originAdcode: adcode,
      seatClass: null,
    });
  });
}

/**
 * Buys a journey and sets off: the learner is in transit until the real travel time has passed.
 * The option and any HanMiles discount are recomputed from the current location and wallet, so a
 * stale UI can never set the price.
 */
export async function purchaseTravelTrip({
  actorDb,
  destinationAdcode,
  destinationUnlocked,
  now,
  optionId,
  redeemHanMiles,
  transport,
}: {
  actorDb: ActorDatabaseHandle;
  destinationAdcode: number;
  destinationUnlocked: boolean;
  now: Date;
  optionId: string;
  /** Spend HanMiles on the largest discount they buy on the journey's flight. */
  redeemHanMiles: boolean;
  transport: ChinaTransport;
}): Promise<string> {
  return actorDb.transaction(async (tx) => {
    const state = await readState(tx, now);
    const origin = requireAt(state);
    if (origin === destinationAdcode) {
      throw new TravelError("already-here");
    }
    if (
      !canTravelTo({
        adcode: destinationAdcode,
        unlocked: destinationUnlocked,
        visited: state.visited,
      })
    ) {
      throw new TravelError("locked");
    }
    const option = resolveTravelOptions(origin, destinationAdcode, transport).find(
      (candidate) => candidate.id === optionId,
    );
    if (option === undefined) {
      throw new TravelError("unknown-option");
    }
    const wallet = await readHanWallet(tx);
    const redemption = redeemHanMiles
      ? travelOptionHanMilesRedemption(option, wallet.hanMiles)
      : null;
    if (redemption !== null && redemption.discountHan === 0) {
      throw new TravelError("no-han-miles-discount");
    }
    const costHan = option.costHan - (redemption?.discountHan ?? 0);
    if (wallet.han < costHan) {
      throw new TravelError("insufficient-han");
    }
    return insertTrip(tx, now, {
      arrivesAt: new Date(now.getTime() + option.durationMinutes * 60_000).toISOString(),
      costHan,
      destinationAdcode,
      distanceKm: option.distanceKm,
      fareFen: Math.round(option.fareCny * 100),
      hanMilesRedeemed: redemption?.hanMilesRedeemed ?? 0,
      mode: option.mode,
      originAdcode: origin,
      seatClass: option.seatClass,
    });
  });
}

/** Instantly returns to a place already visited, for free. */
export async function teleportTo({
  actorDb,
  destinationAdcode,
  now,
}: {
  actorDb: ActorDatabaseHandle;
  destinationAdcode: number;
  now: Date;
}): Promise<string> {
  return actorDb.transaction(async (tx) => {
    const state = await readState(tx, now);
    const origin = requireAt(state);
    if (origin === destinationAdcode) {
      throw new TravelError("already-here");
    }
    if (!state.visited.has(destinationAdcode)) {
      throw new TravelError("not-visited");
    }
    return insertTrip(tx, now, {
      arrivesAt: now.toISOString(),
      costHan: 0,
      destinationAdcode,
      distanceKm: 0,
      fareFen: 0,
      mode: TELEPORT_TRAVEL_MODE,
      originAdcode: origin,
      seatClass: null,
    });
  });
}

/** Rides a dragon: instant travel to any learned or visited place, for one dragon and no Han. */
export async function rideDragonTo({
  actorDb,
  destinationAdcode,
  destinationUnlocked,
  now,
}: {
  actorDb: ActorDatabaseHandle;
  destinationAdcode: number;
  destinationUnlocked: boolean;
  now: Date;
}): Promise<string> {
  return actorDb.transaction(async (tx) => {
    const state = await readState(tx, now);
    const origin = requireAt(state);
    if (origin === destinationAdcode) {
      throw new TravelError("already-here");
    }
    if (
      !canTravelTo({
        adcode: destinationAdcode,
        unlocked: destinationUnlocked,
        visited: state.visited,
      })
    ) {
      throw new TravelError("locked");
    }
    const wallet = await readHanWallet(tx);
    if (wallet.dragons < 1) {
      throw new TravelError("no-dragon");
    }
    return insertTrip(tx, now, {
      arrivesAt: now.toISOString(),
      costHan: 0,
      destinationAdcode,
      distanceKm: 0,
      fareFen: 0,
      mode: DRAGON_TRAVEL_MODE,
      originAdcode: origin,
      seatClass: null,
    });
  });
}

/**
 * Turns back: the learner returns to the origin at once, and the fare is forfeited (the trip row
 * stays, so its cost and any HanMiles redeemed on it still count). The destination does not count
 * as visited, and the journey earns no HanMiles.
 */
export async function cancelTravelTrip({
  actorDb,
  now,
}: {
  actorDb: ActorDatabaseHandle;
  now: Date;
}): Promise<void> {
  await actorDb.transaction(async (tx) => {
    const state = await readState(tx, now);
    if (state.kind !== "in-transit") {
      throw new TravelError("not-in-transit");
    }
    const nowIso = now.toISOString();
    await tx
      .update(actorSchema.travelTrip)
      .set({ cancelledAt: nowIso, updatedAt: nowIso })
      .where(
        and(
          eq(actorSchema.travelTrip.id, state.trip.id),
          isNull(actorSchema.travelTrip.cancelledAt),
        ),
      )
      .run();
  });
}
